import { useState, useCallback, useRef } from 'react';
import { HomeObject } from '../types';

interface DragState {
  isDragging: boolean;
  draggedItem: HomeObject | null;
  startPosition: { x: number; y: number };
  currentPosition: { x: number; y: number };
}

export const useDragAndDrop = (
  homeObjects: HomeObject[],
  onUpdateObject: (id: number, updates: Partial<HomeObject>) => void
) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItem: null,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 }
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const lastUpdateRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseDown = useCallback((event: React.MouseEvent, item: HomeObject) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = event.clientX;
    const startY = event.clientY;

    setDragState({
      isDragging: true,
      draggedItem: item,
      startPosition: { x: startX, y: startY },
      currentPosition: { x: startX, y: startY }
    });

    // Fonction pour gérer le mouvement de la souris
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      // Calculer la nouvelle position en pourcentage
      const newX = Math.max(0, Math.min(100, item.x + (deltaX / rect.width) * 100));
      const newY = Math.max(0, Math.min(100, item.y + (deltaY / rect.height) * 100));

      // Mise à jour immédiate du state pour feedback visuel
      setDragState(prev => ({
        ...prev,
        currentPosition: { x: moveEvent.clientX, y: moveEvent.clientY }
      }));

      // Stocker la dernière position calculée
      lastUpdateRef.current = { x: newX, y: newY };

      // Throttle les mises à jour de l'objet avec RAF
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      animationRef.current = requestAnimationFrame(() => {
        onUpdateObject(item.id, lastUpdateRef.current);
      });
    };

    // Fonction pour gérer le relâchement de la souris
    const handleMouseUp = () => {
      // S'assurer que la dernière position est sauvegardée
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        onUpdateObject(item.id, lastUpdateRef.current);
      }

      setDragState({
        isDragging: false,
        draggedItem: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onUpdateObject]);

  const handleTouchStart = useCallback((event: React.TouchEvent, item: HomeObject) => {
    event.preventDefault();
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const touch = event.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    setDragState({
      isDragging: true,
      draggedItem: item,
      startPosition: { x: startX, y: startY },
      currentPosition: { x: startX, y: startY }
    });

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      const newX = Math.max(0, Math.min(100, item.x + (deltaX / rect.width) * 100));
      const newY = Math.max(0, Math.min(100, item.y + (deltaY / rect.height) * 100));

      // Stocker la dernière position calculée
      lastUpdateRef.current = { x: newX, y: newY };

      // Throttle les mises à jour avec RAF
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      animationRef.current = requestAnimationFrame(() => {
        onUpdateObject(item.id, lastUpdateRef.current);
      });
    };

    const handleTouchEnd = () => {
      // S'assurer que la dernière position est sauvegardée
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        onUpdateObject(item.id, lastUpdateRef.current);
      }

      setDragState({
        isDragging: false,
        draggedItem: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });

      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [onUpdateObject]);

  return {
    dragState,
    containerRef,
    handleMouseDown,
    handleTouchStart
  };
};